/**
 * Builds pre-keyed chip removal handlers for active filter chips.
 *
 * Pure function — takes filter state with setters, returns a record
 * of chip key → reset handler. Chip keys match deriveActiveFilterChips output.
 * @module components/shared/search-modal/utils/buildChipRemoveHandlers
 */

import {
  DEFAULT_AGE_RANGE,
  DEFAULT_CONTRACT_MONTHS_RANGE,
  DEFAULT_CUSTOM_STAT_RANGE,
  DEFAULT_SCORE_RANGE,
  DEFAULT_VALUATION_RANGE,
} from "@/constants/filterDefaults";
import type useFilterState from "@/hooks/useFilterState";

/**
 * Builds handlers that reset each filter category to its default.
 * @param fs - Filter state with setters from useFilterState
 * @returns Record of chip key to removal handler
 */
const buildChipRemoveHandlers = (
  fs: ReturnType<typeof useFilterState>
): Readonly<Record<string, () => void>> => {
  const customStatHandlers = Object.keys(fs.customStatRanges).reduce(
    (acc, name) => ({
      ...acc,
      [`customStat_${name}`]: () =>
        fs.setCustomStatRange(name, DEFAULT_CUSTOM_STAT_RANGE),
    }),
    {} as Record<string, () => void>
  );

  return {
    roles: () => fs.setSelectedRoleArchetypes([]),
    positions: () => fs.setSelectedPositionIds([]),
    clubs: () => fs.setSelectedClubIds([]),
    scouts: () => fs.setSelectedScoutIds([]),
    nationalities: () => fs.setSelectedNationalityIds([]),
    leagues: () => fs.setSelectedLeagueIds([]),
    competitions: () => fs.setSelectedCompetitionIds([]),
    age: () => fs.setAgeRange(DEFAULT_AGE_RANGE),
    contractMonths: () =>
      fs.setContractMonthsRange(DEFAULT_CONTRACT_MONTHS_RANGE),
    valuation: () => fs.setValuationRange(DEFAULT_VALUATION_RANGE),
    gpr: () => fs.setGprRange(DEFAULT_SCORE_RANGE),
    fitScore: () => fs.setFitScoreRange(DEFAULT_SCORE_RANGE),
    teamStyleFit: () => fs.setTeamStyleFitRange(DEFAULT_SCORE_RANGE),
    carrying: () => fs.setCarryingRange(DEFAULT_SCORE_RANGE),
    dribbling: () => fs.setDribblingRange(DEFAULT_SCORE_RANGE),
    passing: () => fs.setPassingRange(DEFAULT_SCORE_RANGE),
    shooting: () => fs.setShootingRange(DEFAULT_SCORE_RANGE),
    gkPassing: () => fs.setGkPassingRange(DEFAULT_SCORE_RANGE),
    gkShotStopping: () => fs.setGkShotStoppingRange(DEFAULT_SCORE_RANGE),
    gkPositioning: () => fs.setGkPositioningRange(DEFAULT_SCORE_RANGE),
    ...customStatHandlers,
  };
};

export default buildChipRemoveHandlers;
